import assert from 'assert'
import { BlockchainError, UnknownError, contractBook, isSupportedChainId } from '@eulerxyz/core-sdk'
import {
  EvcBatchSimulationAggregator,
  type OracleBatchItemFactory,
  type OracleDetailedInfo,
  isEVCCall,
  mapToEVCCall,
  nonNullable,
} from '@eulerxyz/evc-sdk'
import { createSelector } from '@reduxjs/toolkit'
import {
  AbiErrorSignatureNotFoundError,
  type Address,
  BaseError,
  type Chain,
  type Client,
  StateMapping,
  StateOverride,
  type Transport,
  getAddress,
  zeroAddress,
} from 'viem'
import { type GetContractErrorReturnType, encodeFunctionData, getContractError } from 'viem/utils'
import { AccountVaults, selectTransactionAccountVaults } from '../selectors/transactions'
import type { ExtendedConfig } from '../types'
import {
  balanceOverrides,
  mapAccountVaultsData,
  mapBalances,
  mapVaultsData,
  permitOverride,
  reduceCollaterals,
  reduceCurrentAccountVaultAccountInfo,
  reduceHealthScore,
  reduceLiabilityValues,
  reduceTTL,
} from '../utils'
import { GeneralTransactionFactoryUnifier } from './GeneralTransactionFactoryUnifier'
import { type TransactionItems, isRewardStreamsTransaction } from './types'

const selectUniqueAccountVaults = createSelector(
  (accountVaults: AccountVaults[]) => accountVaults,
  (accountVaults) => [
    ...new Map(accountVaults.map((item) => [`${item.account}-${item.vault}`.toLowerCase(), item])).values(),
  ],
)

export interface SimulateProps<transactions extends TransactionItems = TransactionItems> {
  prevTransactions?: transactions
  transactions: transactions
  currentVault?: Address
  currentAccount?: Address
  forceCheckVaults?: Address[]
}

export class SimulationFactory<
  config extends ExtendedConfig = ExtendedConfig,
  transport extends Transport = Transport,
  chain extends Chain = Chain,
> {
  client: Client<transport, chain>
  account: Address
  config: config

  #oracleBatchItemFactory: OracleBatchItemFactory
  // @ts-ignore - Type constraint mismatch
  #evcBatchAggregator: EvcBatchSimulationAggregator<chain, transport>
  #generalTransactionFactoryUnifier: GeneralTransactionFactoryUnifier
  readonly #evcAddress: Address
  readonly #accountLensAddress: Address
  readonly #vaultLensAddress: Address
  readonly #utilsLensAddress: Address

  /**
   * Create a new instance of the SimulationFactory class.
   * @param config {ExtendedConfig} - Wagmi config object.
   * @param client {Client} - The client to use for making batched contract transactions.
   * @param account {Address} - The account to use for making batched contract transactions.
   */
  constructor(config: config, client: Client<transport, chain>, account: Address) {
    this.client = client
    this.account = account
    this.config = config
    assert(isSupportedChainId(client.chain.id), 'UNSUPPORTED_CHAIN_ID')
    this.#oracleBatchItemFactory = config.getOracleBatchItemFactory({
      chainId: client.chain.id,
    })
    this.#evcAddress = contractBook.evc.address[client.chain.id]
    this.#accountLensAddress = contractBook.accountLens.address[client.chain.id]
    this.#vaultLensAddress = contractBook.vaultLens.address[client.chain.id]
    this.#utilsLensAddress = contractBook.utilsLens.address[client.chain.id]

    this.#generalTransactionFactoryUnifier = new GeneralTransactionFactoryUnifier(client.chain.id, contractBook)
    // @ts-ignore - Type constraint mismatch
    this.#evcBatchAggregator = new EvcBatchSimulationAggregator(client)
    this.#evcBatchAggregator.setAccount(this.account)
  }

  /**
   * Simulates transactions and returns the results.
   * @param props {SimulateProps} - The properties for the simulation.
   * @returns A promise that resolves with the simulation results.
   */
  async simulate<transactions extends TransactionItems = TransactionItems>(props: SimulateProps<transactions>) {
    const transactionAccountVaults = selectTransactionAccountVaults(
      [...props.transactions, ...(props.prevTransactions || [])],
      this.account,
    )
    const accountVaultsMix: AccountVaults[] = await fetch(
      `/api/v1/accounts/vaults?chainId=${this.client.chain.id}&account=${this.account}`,
    ).then((response) => response.json())

    const accountVaults = selectUniqueAccountVaults([...accountVaultsMix, ...transactionAccountVaults])
    // Account Vault Info
    const accountVaultInfos = this.#getAccountVaultInfoCalls(accountVaults)

    const accounts = [...new Set(accountVaults.map(({ account }) => getAddress(account)))]
    const vaults = [...new Set(accountVaults.map(({ vault }) => getAddress(vault)))]
    const assets = this.#getAssets(props)
    const oracleBatchItems = await this.#getRequiredOracleBatchItems(accounts, vaults)

    const utilsLens = this.#getDeltaBalances(assets)

    const accountStatus = this.#getAccountStatusCalls(accounts)
    const vaultInfos = this.#getVaultInfoCalls(props.currentVault ? [...vaults, props.currentVault] : vaults)

    let currentVault = this.#getAccountVaultInfoCalls([])
    if (props.currentVault && props.currentAccount) {
      currentVault = this.#getAccountVaultInfoCalls([{ account: props.currentAccount, vault: props.currentVault }])
    }
    // Accounts Info
    const accountInfos = this.#getAccountInfoCalls(accountVaults)

    let currentAccountInfos = this.#getAccountInfoCalls([])
    if (props.currentVault && props.currentAccount) {
      currentAccountInfos = this.#getAccountInfoCalls([{ account: props.currentAccount, vault: props.currentVault }])
    }

    const { configs: prevTransactionsConfig, positions: prevTransactionsPositions } =
      this.#generalTransactionFactoryUnifier.typeGuardConvertMultipleWithPositions(props.prevTransactions || [])

    const { configs: transactionsConfig, positions: transactionsPositions } =
      this.#generalTransactionFactoryUnifier.typeGuardConvertMultipleWithPositions(props.transactions)

    const stateOverrides = await this.#getStateOverrides(
      props,
      [...oracleBatchItems, ...prevTransactionsConfig, ...transactionsConfig].reduce(
        (acc, item) => acc + item.value,
        0n,
      ),
    )
    this.#evcBatchAggregator.setStateOverride(stateOverrides)
    try {
      const [
        _,
        // BEFORE
        prevTransactions,
        balancesBefore,
        vaultsBefore,
        [currentVaultBefore],
        accountStatusBefore,
        accountVaultInfosBefore,
        accountInfosBefore,
        accountInfoBefore,
        // AFTER
        transactions,
        balancesAfter,
        vaultsAfter,
        [currentVaultAfter],
        accountStatusAfter,
        accountVaultInfosAfter,
        accountInfosAfter,
        accountInfoAfter,
      ] = await Promise.all([
        /// oracle items
        Promise.all(oracleBatchItems.map((call) => this.#evcBatchAggregator.addRawCall(call))),

        //////////////////////////////////
        /// BEFORE STATE
        /////////////////////////////////

        // prev transactions
        Promise.all(prevTransactionsConfig.map((call) => this.#evcBatchAggregator.add(call))),
        /// balances
        this.#evcBatchAggregator.addValue(utilsLens),
        /// vault infos
        Promise.all(vaultInfos.map((call) => this.#evcBatchAggregator.addValue(call))),
        /// current vault
        Promise.all(currentVault.map((call) => this.#evcBatchAggregator.addValue(call))),
        /// account status
        Promise.all(accountStatus.map((call) => this.#evcBatchAggregator.addValue(call))),
        /// account vault infos
        Promise.all(accountVaultInfos.map((call) => this.#evcBatchAggregator.addValue(call))),
        /// Account infos
        Promise.all(accountInfos.map((call) => this.#evcBatchAggregator.addValue(call))),
        /// Current infos
        Promise.all(currentAccountInfos.map((call) => this.#evcBatchAggregator.addValue(call))),

        //////////////////////////////////
        /// AFTER STATE
        /////////////////////////////////

        /// transactions
        Promise.all(transactionsConfig.map((call) => this.#evcBatchAggregator.add(call))),
        /// balances
        this.#evcBatchAggregator.addValue(utilsLens),
        /// vault infos
        Promise.all(vaultInfos.map((call) => this.#evcBatchAggregator.addValue(call))),
        /// current vault
        Promise.all(currentVault.map((call) => this.#evcBatchAggregator.addValue(call))),
        /// account status
        Promise.all(accountStatus.map((call) => this.#evcBatchAggregator.addValue(call))),
        /// account vault infos
        Promise.all(accountVaultInfos.map((call) => this.#evcBatchAggregator.addValue(call))),
        /// Account infos
        Promise.all(accountInfos.map((call) => this.#evcBatchAggregator.addValue(call))),
        /// Current infos
        Promise.all(currentAccountInfos.map((call) => this.#evcBatchAggregator.addValue(call))),
      ])

      const encodedCall = encodeFunctionData({
        abi: contractBook.evc.abi,
        functionName: 'batchSimulation',
        args: [mapToEVCCall(transactionsConfig)],
      })

      const mappedPrevTransactions = this.#generalTransactionFactoryUnifier.mapResultsBack(
        props.prevTransactions || [],
        prevTransactions,
        prevTransactionsPositions,
      )

      const mappedTransactions = this.#generalTransactionFactoryUnifier.mapResultsBack(
        props.transactions,
        transactions,
        transactionsPositions,
      )

      const accountStatusResults = this.#evcBatchAggregator.getAccountStatusResults()
      const vaultStatusResults = this.#evcBatchAggregator.getVaultStatusResults()

      const transactionsFirstError = transactions.find((transaction, i) => {
        if (transaction.status === 'failure') {
          console.log('[SimulationFactory] transaction', transactionsConfig[i], ' state ', transaction.error)
        }
        return transaction.status === 'failure'
      })

      const prevTransactionsFirstError = prevTransactions.find((transaction, i) => {
        if (transaction.status === 'failure') {
          console.log('[SimulationFactory] prev transaction', prevTransactionsConfig[i], ' state ', transaction.error)
        }
        return transaction.status === 'failure'
      })

      return {
        prevTransactions: mappedPrevTransactions,
        transactions: mappedTransactions,
        unmappedTransactions: transactions,
        unmappedPrevTransactions: prevTransactions,
        transactionsConfig,
        accountStatusResults,
        vaultStatusResults,
        currentVaultBefore,
        currentVaultAfter,
        vaultsBefore: mapVaultsData(vaultsBefore),
        vaultsAfter: mapVaultsData(vaultsAfter),
        vaults: vaultsAfter,
        encodedCall,
        stateOverrides,
        prevTransactionsFirstError,
        transactionsFirstError,
        accountStatusBefore,
        accountStatusAfter,
        currentAccountVaultAccountInfoBefore: reduceCurrentAccountVaultAccountInfo(
          accountStatusBefore,
          props.currentAccount,
        ),
        currentAccountVaultAccountInfoAfter: reduceCurrentAccountVaultAccountInfo(
          accountStatusAfter,
          props.currentAccount,
        ),
        calls: mapToEVCCall(transactionsConfig),
        liabilityValueBefore: reduceLiabilityValues(accountStatusBefore),
        liabilityValueAfter: reduceLiabilityValues(accountStatusAfter),
        collateralsBefore: reduceCollaterals(accountStatusBefore),
        collateralsAfter: reduceCollaterals(accountStatusAfter),
        healthScoreBefore: reduceHealthScore(accountStatusBefore),
        healthScoreAfter: reduceHealthScore(accountStatusAfter),
        ttlBefore: reduceTTL(accountStatusBefore),
        ttlAfter: reduceTTL(accountStatusAfter),
        receivers: accounts,
        accountVaultInfosBefore: mapAccountVaultsData(accountVaultInfosBefore),
        accountVaultInfosAfter: mapAccountVaultsData(accountVaultInfosAfter),
        accountInfoBefore,
        accountInfoAfter,
        accountInfosBefore,
        accountInfosAfter,
        balancesBefore: mapBalances(assets, balancesBefore),
        balancesAfter: mapBalances(assets, balancesAfter),
        currentAccount: props.currentAccount,
      } as const
    } catch (err) {
      if (err instanceof BaseError) {
        let error: GetContractErrorReturnType | undefined
        error = getContractError(err, {
          abi: contractBook.evc.abi,
          address: this.#evcAddress,
          args: [mapToEVCCall(transactionsConfig)],
          functionName: 'batchSimulation',
          sender: this.account,
        })
        if ((error as any)?.cause?.cause instanceof AbiErrorSignatureNotFoundError) {
          for (const transaction of transactionsConfig) {
            error = getContractError(err, {
              abi: isEVCCall(transaction) ? contractBook.evc.abi : transaction.abi,
              address: this.#evcAddress,
              args: [mapToEVCCall(transactionsConfig)],
              functionName: 'batchSimulation',
              sender: this.account,
            })
            if ((error as any)?.cause?.cause instanceof AbiErrorSignatureNotFoundError) {
              continue
            }
            break
          }
        }

        throw new BlockchainError('simulationFactory', 'Error on the simulated transaction', error)
      }

      throw new UnknownError('simulationFactory', 'Unknown error on simulation', err as Error)
    }
  }

  /**
   * Gets state overrides for the simulation.
   * @param params {Pick<SimulateProps<transactions>, "prevTransactions" | "transactions">} - The previous and current transactions.
   * @returns The state overrides.
   */
  #getStateOverrides<transactions extends TransactionItems = TransactionItems>(
    { prevTransactions, transactions }: Pick<SimulateProps<transactions>, 'prevTransactions' | 'transactions'>,
    value = 0n,
  ): Promise<StateOverride> {
    const props = {
      transactions: [...(prevTransactions || []), ...transactions],
      chainId: this.client.chain.id,
      address: this.account,
    }

    const fromAddressValues = new Map<Address, bigint>([[this.account, value]])
    for (const tx of props.transactions) {
      if ('from' in tx) {
        const from = getAddress(tx.from)
        const currentValue = fromAddressValues.get(from) || value
        const txValue = 'value' in tx ? BigInt(tx.value || 0) : 0n
        fromAddressValues.set(from, currentValue + txValue)
      }
    }

    const addressOverrides = Array.from(fromAddressValues.entries()).map(([address, value]) => ({
      address,
      balance: value, // total value sent by this address
    }))

    return Promise.all([permitOverride(this.config, props), balanceOverrides(this.config, props)]).then(
      ([permits, balances]) => {
        const _overrides = [...permits, ...balances]

        const overrides = new Map<Address, StateMapping>()

        for (const override of _overrides) {
          if (override.stateDiff) {
            const current = overrides.get(getAddress(override.address)) || []
            overrides.set(getAddress(override.address), [...current, ...override.stateDiff])
          }
        }

        const result = Array.from(overrides.entries()).map(([address, stateDiff]) => ({
          address,
          stateDiff,
        }))
        const final: StateOverride = [...result, ...addressOverrides]
        return final
      },
    )
  }

  /**
   * Gets the list of accounts involved in the transactions.
   * @param params {Pick<SimulateProps<transactions>, "prevTransactions" | "transactions">} - The previous and current transactions.
   * @returns The list of accounts.
   */
  // @ts-ignore - Unused method
  #getAccounts<transactions extends TransactionItems = TransactionItems>({
    prevTransactions,
    transactions,
    currentAccount,
  }: Pick<SimulateProps<transactions>, 'prevTransactions' | 'transactions' | 'currentAccount'>) {
    const accountVaults = selectTransactionAccountVaults([...(prevTransactions || []), ...transactions], this.account)

    const accounts = new Set(accountVaults.map(({ account }) => account))
    if (currentAccount) {
      accounts.add(currentAccount)
    }

    return Array.from(new Set(accounts))
  }

  /**
   * Gets the list of vaults involved in the transactions.
   * @param params {Pick<SimulateProps<transactions>, "prevTransactions" | "transactions">} - The previous and current transactions.
   * @returns The list of vaults.
   */
  // @ts-ignore - Unused method
  #getVaults<transactions extends TransactionItems = TransactionItems>({
    prevTransactions,
    transactions,
    forceCheckVaults,
  }: Pick<SimulateProps<transactions>, 'prevTransactions' | 'transactions' | 'forceCheckVaults'>) {
    const vaults: Address[] = forceCheckVaults || []
    for (const transaction of [...(prevTransactions || []), ...transactions]) {
      if ('vault' in transaction) vaults.push(transaction.vault)
      if ('vaultIn' in transaction) vaults.push(transaction.vaultIn)
      if ('fromVault' in transaction) vaults.push(transaction.fromVault)
      if ('toVault' in transaction) vaults.push(transaction.toVault)
      if (isRewardStreamsTransaction(transaction)) vaults.push(transaction.rewarded)

      if ('transactions' in transaction && transaction?.transactions) {
        vaults.push(...this.#getVaults({ transactions: transaction.transactions }))
      }
    }
    return Array.from(new Set(vaults))
  }

  #getAssets<transactions extends TransactionItems = TransactionItems>({
    prevTransactions,
    transactions,
  }: Pick<SimulateProps<transactions>, 'prevTransactions' | 'transactions'>) {
    const assets: Address[] = []
    for (const transaction of [...(prevTransactions || []), ...transactions]) {
      if ('asset' in transaction) assets.push(transaction.asset.address)
      if ('transactions' in transaction && transaction?.transactions) {
        assets.push(...this.#getAssets({ transactions: transaction.transactions }))
      }
    }
    return Array.from(new Set(assets))
  }

  /**
   * Gets the required oracle batch items for the simulation.
   * @param accounts {Address[]} - The list of accounts.
   * @param vaults {Address[]} - The list of vaults.
   * @returns A promise that resolves with the required oracle batch items.
   */
  async #getRequiredOracleBatchItems(accounts: Address[], vaults: Address[]) {
    const enabledVaults = await this.#getAccountEnabledVaults(accounts)

    return this.#createOracleBatchItems([...vaults, ...enabledVaults])
  }

  /**
   * Gets the enabled vaults for the given accounts.
   * @param accounts {Address} - The list of accounts.
   * @returns A promise that resolves with the list of enabled vaults.
   */
  async #getAccountEnabledVaults(accounts: Address[]) {
    return this.#evcBatchAggregator.batchSimulation(this.#getAccountStatusCalls(accounts)).then((results) => {
      const enabledVaults: Address[] = []
      for (const result of results.results) {
        if (result.status === 'success') {
          enabledVaults.push(...result.result.evcAccountInfo.enabledCollaterals)
          enabledVaults.push(...result.result.evcAccountInfo.enabledControllers)
        }
      }
      return enabledVaults
    })
  }

  /**
   * Creates oracle batch items for the given vaults.
   * @param vaults {Address[]} - The list of vaults.
   * @returns A promise that resolves with the oracle batch items.
   */
  async #createOracleBatchItems(vaults: Address[]) {
    /// this means that all vaults were cached
    if (!vaults.length) return this.#oracleBatchItemFactory.createBatchItems([], this.account, true)

    const oracleInfos: OracleDetailedInfo[] = await this.#evcBatchAggregator
      .batchSimulation(this.#getVaultInfoCalls(vaults))
      .then((results) => {
        const infos: OracleDetailedInfo[] = []
        for (const result of results.results) {
          if (result.status === 'success') {
            if (result.result.oracleInfo.oracle !== zeroAddress) {
              infos.push({
                ...result.result.oracleInfo,
                vault: result.result.vault,
              })
            } else if (result.result.backupAssetOracleInfo.oracle !== zeroAddress) {
              infos.push({
                ...result.result.backupAssetOracleInfo,
                vault: result.result.vault,
              })
            }
          }
        }

        return infos
      })
    return this.#oracleBatchItemFactory.createBatchItems(oracleInfos, this.account, true)
  }

  /**
   * Gets the account status calls for the given accounts.
   * @param accounts {Address[]} - The list of accounts.
   * @returns The account status calls.
   */
  #getAccountStatusCalls(accounts: Address[]) {
    return accounts.map(
      (account) =>
        ({
          abi: contractBook.accountLens.abi,
          address: this.#accountLensAddress,
          functionName: 'getAccountEnabledVaultsInfo',
          args: [this.#evcAddress, account],
          onBehalfOfAccount: this.account,
          value: 0n,
        }) as const,
    )
  }

  /**
   * Gets the vault info calls for the given vaults.
   * @param vaults {Address[]} - The list of vaults.
   * @returns The vault info calls.
   */
  #getVaultInfoCalls(vaults: Address[]) {
    return vaults.map(
      (vault) =>
        ({
          abi: contractBook.vaultLens.abi,
          address: this.#vaultLensAddress,
          functionName: 'getVaultInfoFull',
          args: [vault],
          onBehalfOfAccount: this.account,
          value: 0n,
        }) as const,
    )
  }

  /**
   * Gets the account info calls for the given accounts-vault pairs.
   * @param accountVaults {{ account: Address; vault: Address }[]} - The list of account-vault pairs.
   * @returns The account info status calls.
   */
  #getAccountInfoCalls(accountVaults: { account: Address; vault: Address }[]) {
    return accountVaults.map(({ account, vault }) => {
      return {
        abi: contractBook.accountLens.abi,
        address: this.#accountLensAddress,
        functionName: 'getAccountInfo',
        sender: account,
        args: [account, vault],
        onBehalfOfAccount: this.account,
        value: 0n,
      } as const
    })
  }

  /**
   * Gets the account vault info calls for the given account-vault pairs.
   * @param accountVaults {{ account: Address; vault: Address }[]} - The list of account-vault pairs.
   * @returns The account vault info calls.
   */
  #getAccountVaultInfoCalls(accountVaults: AccountVaults[]) {
    return accountVaults.flatMap(({ account, vault }) => {
      const call = {
        abi: contractBook.accountLens.abi,
        address: this.#accountLensAddress,
        functionName: 'getVaultAccountInfo',
        args: [account, vault || zeroAddress],
        onBehalfOfAccount: this.account,
        value: 0n,
      } as const

      if (getAddress(this.account) === getAddress(account)) return call
      return [call, { ...call, args: [this.account, vault || zeroAddress] } as const]
    })
  }

  #getDeltaBalances(assets: Address[]) {
    return {
      abi: contractBook.utilsLens.abi,
      address: this.#utilsLensAddress,
      functionName: 'tokenBalances',
      args: [this.account, assets.filter(nonNullable)],
      onBehalfOfAccount: this.account,
      value: 0n,
    } as const
  }
}
